/**
 * WeaveIn.ai Platform - Promote Contact to Member Dialog
 *
 * Promotes a contact (record_type 'contact') to a full member without
 * requiring a program enrollment. Before promoting it runs a deduplication
 * check; if a likely-existing member/contact is found the user is warned and
 * can either merge into the existing record or promote anyway.
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, GitMerge, Loader2, UserCheck, X } from 'lucide-react';
import { contactsAPI, Contact, DuplicateCheckResult } from '@/services/api/contacts';

interface PromoteToMemberDialogProps {
  contact: Contact;
  onClose: () => void;
  /** Called after a successful promote OR merge. The contact is now a member. */
  onSuccess: (memberId: number) => void;
}

export default function PromoteToMemberDialog({ contact, onClose, onSuccess }: PromoteToMemberDialogProps) {
  const [checking, setChecking] = useState(true);
  const [matches, setMatches] = useState<DuplicateCheckResult[]>([]);
  const [memberMatchCount, setMemberMatchCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    contactsAPI
      .promotionCheck(contact.id)
      .then((res) => {
        if (!active) return;
        setMatches(res.matches || []);
        setMemberMatchCount(res.member_match_count || 0);
      })
      .catch(() => {
        // A failed dedup check should not block promotion — just proceed
        // without warnings.
        if (active) setMatches([]);
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, [contact.id]);

  const handlePromote = async () => {
    setProcessing(true);
    setError(null);
    try {
      await contactsAPI.promoteContact(contact.id);
      onSuccess(contact.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to promote contact');
      setProcessing(false);
    }
  };

  const handleMerge = async (targetId: number) => {
    setProcessing(true);
    setError(null);
    try {
      await contactsAPI.mergeContact(contact.id, targetId);
      // The source contact was merged into the target member/contact record.
      onSuccess(targetId);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to merge contact');
      setProcessing(false);
    }
  };

  const hasMatches = matches.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <UserCheck className="h-5 w-5 text-green-600" /> Promote to Member
          </h3>
          <button onClick={onClose} disabled={processing} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
            Promote <span className="font-medium">{contact.first_name} {contact.last_name || ''}</span> to a full member.
          </p>

          {checking ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking for existing records…
            </div>
          ) : hasMatches ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800 dark:border-orange-900/40 dark:bg-orange-900/20 dark:text-orange-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  {memberMatchCount > 0
                    ? 'This contact may already exist as a member. Merge into the existing record to avoid a duplicate, or promote anyway.'
                    : 'A similar record already exists. Review before promoting.'}
                </span>
              </div>

              {matches.map((m) => (
                <div key={m.member_id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {m.first_name} {m.last_name || ''}
                        </span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                            m.record_type === 'member'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}
                        >
                          {m.record_type === 'member' ? 'Member' : 'Contact'}
                        </span>
                        <span className="text-xs font-semibold text-orange-600">{Math.round(m.match_score)}% match</span>
                      </div>
                      <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                        {m.member_number && <div>#{m.member_number}</div>}
                        {m.email_masked && <div>{m.email_masked}</div>}
                        {m.phone_masked && <div>{m.phone_masked}</div>}
                        {m.date_of_birth && <div>DOB: {m.date_of_birth}</div>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleMerge(m.member_id)}
                      disabled={processing}
                      className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-blue-300 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-800 dark:text-blue-300"
                    >
                      <GitMerge className="h-3.5 w-3.5" /> Merge into this
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-300">
              No likely duplicates found. This contact can be safely promoted.
            </div>
          )}

          {error && (
            <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={processing}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handlePromote}
            disabled={processing || checking}
            className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
            {hasMatches ? 'Promote anyway' : 'Promote to Member'}
          </button>
        </div>
      </div>
    </div>
  );
}
